import * as Yup from 'yup';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Field, Formik } from 'formik';
import { Grid, Skeleton } from '@mui/material';

import {
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroYesNo,
	CerebroMaskedInput,
	CerebroForm,
} from '../shared/components';
import { useApi } from '../shared/context';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { Deal, FinancialStatementType, FinancialStatementTypeMap, UpdateDealDto } from '../shared/models';
import { QueryKeys, useDealQuery, useNavigateToNextPage } from '../shared/hooks';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { MaxCurrencyLength } from '../shared/utils/platform';
import { getApiResponseData } from '../shared/utils/axios';

export const BorrowerBasicInfo: React.FC = () => {
	const snackbar = useSnackbar();

	const api = useApi();

	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const queryClient = useQueryClient();

	const dealQuery = useDealQuery(guid);

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const dealPatch: UpdateDealDto = {
				...values,
				companyFinancialStatementType: values.financialStatementType,
				purchaseOrderAverageMonthlyCost:
					NumberUtils.ParseDecimal(values.purchaseOrderAverageMonthlyCost) ?? null,
			};

			const { data, error } = await getApiResponseData<Deal>(
				api.patch(CerebroApiEndpoints.Deals.Root(guid), dealPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: deal => {
				queryClient.setQueryData(QueryKeys.Deal.Root(guid), deal);
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Financials &gt; Borrower &gt; Basic Info</CerebroBreadcrumb>
			<CerebroHeading>Tell us about your company’s finances.</CerebroHeading>
			<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
				Financials, such as income statement and balance sheet metrics, will be used to calculate your debt
				capacity, credit strength, and available loan options.
			</CerebroText>

			<Formik
				validationSchema={BorrowerBasicInfoSchema}
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(dealMutation.mutate)}
				initialValues={{
					currency: dealQuery.data?.currency ?? 'USD',
					financialStatementType: dealQuery.data?.companyFinancialStatementType ?? 'None',
					customerPurchaseOrders: dealQuery.data?.customerPurchaseOrders ?? null,
					purchaseOrderAverageMonthlyCost: NumberUtils.FormatCurrency(
						dealQuery.data?.purchaseOrderAverageMonthlyCost
					),
				}}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<CerebroLabel htmlFor="financialStatementType" sx={{ marginTop: 6 }}>
							What type of financial statements does the company have?
						</CerebroLabel>
						<Grid container spacing={3}>
							<Grid item xs={6}>
								{dealQuery.isSuccess ? (
									<Field
										id="financialStatementType"
										as={CerebroDropdown}
										name="financialStatementType"
										disabled={isSubmitting}
									>
										{Object.entries(FinancialStatementTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="customerPurchaseOrders" sx={{ marginTop: 6 }}>
							Do your customers execute purchase orders?
						</CerebroLabel>
						<Field
							id="customerPurchaseOrders"
							as={CerebroYesNo}
							name="customerPurchaseOrders"
							disabled={isSubmitting}
						/>

						{values.customerPurchaseOrders && (
							<Grid container spacing={3}>
								<Grid item xs={6}>
									<CerebroLabel htmlFor="purchaseOrderAverageMonthlyCost" sx={{ marginTop: 6 }}>
										Average aggregate monthly cost to fulfill POs?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="purchaseOrderAverageMonthlyCost"
										name="purchaseOrderAverageMonthlyCost"
										disabled={isSubmitting}
									/>
								</Grid>
							</Grid>
						)}

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save &amp; Continue
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroPageContainer>
	);
};

const BorrowerBasicInfoSchema = Yup.object().shape({
	currency: Yup.string(),
	financialStatementType: Yup.string(),
	customerPurchaseOrders: Yup.boolean(),
});

type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<BorrowerBasicInfoFormValues>;
interface BorrowerBasicInfoFormValues {
	currency: string;
	financialStatementType: FinancialStatementType;
	customerPurchaseOrders: boolean | null;
	purchaseOrderAverageMonthlyCost: string;
}
